import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Flame } from "lucide-react";
import { packages } from "@/data";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import UpsellModal from "@/components/UpsellModal";
import { API, axios } from "@/lib/api";

const LOOKUP = {
  launch: "launch_package",
  business: "business_package",
  "brand-business": "brand_business_package",
  "launch-everything": "launch_everything_package",
};

const Packages = () => {
  const [modalPkg, setModalPkg] = useState(null);
  const [capacity, setCapacity] = useState(null);

  useEffect(() => {
    axios.get(`${API}/capacity/launch`).then(({ data }) => setCapacity(data)).catch(() => {});
  }, []);

  return (
    <>
    <section id="pricing" className="py-24 md:py-32 relative" data-testid="packages">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewport} className="mb-14 md:mb-20 max-w-3xl">
          <p className="text-[#ccff00] text-xs font-semibold uppercase tracking-widest mb-4">One-off packages</p>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
            Not a website. A complete business presence.
          </h2>
          <p className="text-lg text-zinc-400">
            Pick a package, send us your details, and we do the rest — live in 48 hours to 7 days depending on scope. No hidden fees, no lock-in.
          </p>
        </motion.div>

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="packages-grid">
          {packages.map((p) => (
            <motion.div
              key={p.id}
              variants={fadeUp}
              whileHover={{ y: -8 }}
              className={`relative rounded-2xl p-7 flex flex-col ${
                p.hero
                  ? p.id === "launch-everything"
                    ? "bg-[#ccff00] text-black"
                    : "bg-[#121212] border border-[#ccff00]/60 volt-glow"
                  : "bg-[#0f0f0f] border border-white/10"
              }`}
              data-testid={`package-${p.id}`}
            >
              {p.badge && (
                <span className={`absolute -top-3 left-7 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${p.id === "launch-everything" ? "bg-black text-[#ccff00]" : "bg-[#ccff00] text-black"}`}>
                  {p.badge}
                </span>
              )}
              <h3 className={`font-display font-bold text-xl mb-1 ${p.hero && p.id === "launch-everything" ? "text-black" : "text-white"}`}>{p.name}</h3>
              <p className={`text-sm mb-5 ${p.hero && p.id === "launch-everything" ? "text-black/70" : "text-zinc-400"}`}>{p.tagline}</p>
              {p.id === "launch" && capacity && (
                <p className={`text-xs font-semibold uppercase tracking-widest mb-4 flex items-center gap-1.5 ${capacity.full ? "text-[#ff0055]" : "text-[#ccff00]"}`} data-testid="launch-capacity-badge">
                  <Flame size={13} />
                  {capacity.full ? "Today's 48h slots are full — next slot: tomorrow" : `${capacity.slots_left}/${capacity.cap} 48h slots left today`}
                </p>
              )}
              <div className="flex items-end gap-2 mb-6">
                <span className={`font-display font-black text-4xl ${p.hero && p.id === "launch-everything" ? "text-black" : "text-white"}`}>{p.price}</span>
                <span className={`text-xs mb-1.5 uppercase tracking-widest ${p.hero && p.id === "launch-everything" ? "text-black/60" : "text-zinc-500"}`}>{p.priceNote}</span>
              </div>
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {p.features.map((f) => (
                  <li key={f} className={`flex items-start gap-2.5 text-sm ${p.hero && p.id === "launch-everything" ? "text-black/80" : "text-zinc-300"}`}>
                    <Check size={16} className={`mt-0.5 shrink-0 ${p.hero && p.id === "launch-everything" ? "text-black" : "text-[#ccff00]"}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setModalPkg({ name: p.name, price: p.price, lookup: LOOKUP[p.id] })}
                className={`rounded-full font-bold uppercase tracking-widest text-xs py-3.5 transition-transform hover:scale-105 ${
                  p.id === "launch-everything"
                    ? "bg-black text-[#ccff00]"
                    : p.hero
                    ? "bg-[#ccff00] text-black"
                    : "border border-white/20 text-white hover:bg-white/5"
                }`}
                data-testid={`package-cta-${p.id}`}
              >
                Get {p.name}
              </button>
            </motion.div>
          ))}
        </motion.div>

        <p className="text-center text-sm text-zinc-500 mt-8">
          Prices are starting points · pay securely by card · WhatsApp us if you're unsure which package fits.
        </p>
      </div>
    </section>
    {modalPkg && <UpsellModal pkg={modalPkg} onClose={() => setModalPkg(null)} />}
    </>
  );
};

export default Packages;
